"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useContext, useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { AnimatePresence, motion } from "framer-motion";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { WaveLeaderboardTime } from "@/components/waves/leaderboard/WaveLeaderboardTime";
import { WaveLeaderboardHeader } from "@/components/waves/leaderboard/header/WaveleaderboardHeader";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import { WaveLeaderboardDrops } from "@/components/waves/leaderboard/drops/WaveLeaderboardDrops";
import { WaveLeaderboardGallery } from "@/components/waves/leaderboard/gallery/WaveLeaderboardGallery";
import { WaveLeaderboardGrid } from "@/components/waves/leaderboard/grid/WaveLeaderboardGrid";
import { isLeaderboardViewMode, type LeaderboardViewMode } from "@/components/waves/leaderboard/types";
import { useWave } from "@/hooks/useWave";
import { AuthContext } from "@/components/auth/Auth";
import { useLayout } from "./layout/LayoutContext";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import useLocalPreference from "@/hooks/useLocalPreference";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useWaveCurationGroups } from "@/hooks/waves/useWaveCurationGroups";
import { getWaveDropEligibility } from "@/components/waves/leaderboard/dropEligibility";
interface MyStreamWaveLeaderboardProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveLeaderboard: React.FC<MyStreamWaveLeaderboardProps> = t0 => {
  const $ = _c(75);
  const {
    wave,
    onDropClick
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    isMemesWave,
    isCurationWave,
    participation
  } = useWave(wave);
  const {
    leaderboardViewStyle
  } = useLayout();
  const mountedRef = useRef(true);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      mountedRef.current = false;
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const [isCreateDropOpen, setIsCreateDropOpen] = useState(false);
  const [isMemesCreateOpen, setIsMemesCreateOpen] = useState(false);
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t3 = Boolean(activeProfileProxy);
  let t4;
  if ($[2] !== isCurationWave || $[3] !== isLoggedIn || $[4] !== participation || $[5] !== t3) {
    t4 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t3,
      isCurationWave,
      participation
    });
    $[2] = isCurationWave;
    $[3] = isLoggedIn;
    $[4] = participation;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    canCreateDrop
  } = t4;
  const showPersistentDropInput = !isMemesWave && isCurationWave && canCreateDrop;
  const showToggleableDropInput = !isMemesWave && !isCurationWave && isCreateDropOpen;
  let t5;
  if ($[7] !== isCurationWave || $[8] !== isMemesWave) {
    t5 = () => {
      if (!mountedRef.current) {
        return;
      }
      if (isMemesWave) {
        setIsMemesCreateOpen(true);
        return;
      }
      if (!isCurationWave) {
        setIsCreateDropOpen(true);
      }
    };
    $[7] = isCurationWave;
    $[8] = isMemesWave;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const onCreateDrop = t5;
  const viewPreferenceKey = `waveViewMode_${wave.id}`;
  const defaultViewMode = isMemesWave ? "grid" : "list";
  const [viewMode, setViewMode] = useLocalPreference(viewPreferenceKey, defaultViewMode, isLeaderboardViewMode);
  const sortPreferenceKey = `waveSortMode_${wave.id}`;
  const [sort, setSort] = useLocalPreference(sortPreferenceKey, WaveDropsLeaderboardSort.RANK, _temp);
  const t6 = wave.wave.type !== ApiWaveType.Chat;
  let t7;
  if ($[10] !== t6 || $[11] !== wave.id) {
    t7 = {
      waveId: wave.id,
      enabled: t6
    };
    $[10] = t6;
    $[11] = wave.id;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    data: t8,
    isLoading: isLoadingCurationGroups,
    isError: isCurationGroupsError
  } = useWaveCurationGroups(t7);
  let t9;
  if ($[13] !== t8) {
    t9 = t8 === undefined ? [] : t8;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const curationGroups = t9;
  let t10;
  if ($[15] !== searchParams) {
    t10 = searchParams.get("curated_by_group");
    $[15] = searchParams;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const rawCuratedByGroupId = t10;
  const curationGroupIdSet = new Set(curationGroups.map(_temp2));
  let t11;
  bb0: {
    if (!rawCuratedByGroupId) {
      t11 = undefined;
      break bb0;
    }
    if (isCurationGroupsError) {
      t11 = undefined;
      break bb0;
    }
    if (isLoadingCurationGroups) {
      t11 = rawCuratedByGroupId;
      break bb0;
    }
    t11 = curationGroupIdSet.has(rawCuratedByGroupId) ? rawCuratedByGroupId : undefined;
  }
  const curatedByGroupId = t11;
  let t12;
  if ($[17] !== pathname || $[18] !== router || $[19] !== searchParams) {
    t12 = groupId => {
      const nextParams = new URLSearchParams(searchParams.toString());
      if (groupId) {
        nextParams.set("curated_by_group", groupId);
      } else {
        nextParams.delete("curated_by_group");
      }
      const nextQuery = nextParams.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[17] = pathname;
    $[18] = router;
    $[19] = searchParams;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const updateCurationGroupInUrl = t12;
  let t13;
  bb1: {
    if (!isMemesWave) {
      t13 = viewMode;
      break bb1;
    }
    if (viewMode === "grid_content_only") {
      t13 = "grid";
      break bb1;
    }
    t13 = viewMode;
  }
  const effectiveViewMode = t13;
  let leaderboardContent;
  if (effectiveViewMode === "list") {
    const t14 = isMemesWave || !isCurationWave ? onCreateDrop : undefined;
    let t15;
    if ($[21] !== curatedByGroupId || $[22] !== sort || $[23] !== t14 || $[24] !== wave) {
      t15 = <WaveLeaderboardDrops wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} onCreateDrop={t14} />;
      $[21] = curatedByGroupId;
      $[22] = sort;
      $[23] = t14;
      $[24] = wave;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    leaderboardContent = t15;
  } else {
    if (!isMemesWave) {
      const t14 = effectiveViewMode === "grid" ? "compact" : "content_only";
      let t15;
      if ($[26] !== curatedByGroupId || $[27] !== onDropClick || $[28] !== sort || $[29] !== t14 || $[30] !== wave) {
        t15 = <WaveLeaderboardGrid wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} mode={t14} onDropClick={onDropClick} />;
        $[26] = curatedByGroupId;
        $[27] = onDropClick;
        $[28] = sort;
        $[29] = t14;
        $[30] = wave;
        $[31] = t15;
      } else {
        t15 = $[31];
      }
      leaderboardContent = t15;
    } else {
      let t14;
      if ($[32] !== curatedByGroupId || $[33] !== onDropClick || $[34] !== sort || $[35] !== wave) {
        t14 = <WaveLeaderboardGallery wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} onDropClick={onDropClick} />;
        $[32] = curatedByGroupId;
        $[33] = onDropClick;
        $[34] = sort;
        $[35] = wave;
        $[36] = t14;
      } else {
        t14 = $[36];
      }
      leaderboardContent = t14;
    }
  }
  let t14;
  if ($[37] !== wave) {
    t14 = <WaveLeaderboardTime wave={wave} />;
    $[37] = wave;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== setViewMode) {
    t15 = mode => setViewMode(mode);
    $[39] = setViewMode;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  const t16 = isMemesWave || !isCurationWave ? onCreateDrop : undefined;
  let t17;
  if ($[41] !== setSort) {
    t17 = s => setSort(s);
    $[41] = setSort;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const t18 = curatedByGroupId ?? null;
  const t19 = curationGroups.length > 0 ? updateCurationGroupInUrl : undefined;
  let t20;
  if ($[43] !== curationGroups || $[44] !== effectiveViewMode || $[45] !== sort || $[46] !== t15 || $[47] !== t16 || $[48] !== t17 || $[49] !== t18 || $[50] !== t19 || $[51] !== wave) {
    t20 = <div className="tw-sticky tw-top-0 tw-z-30 tw-bg-black tw-py-4"><WaveLeaderboardHeader wave={wave} viewMode={effectiveViewMode} sort={sort} onViewModeChange={t15} onCreateDrop={t16} onSortChange={t17} curationGroups={curationGroups} curatedByGroupId={t18} onCurationGroupChange={t19} /></div>;
    $[43] = curationGroups;
    $[44] = effectiveViewMode;
    $[45] = sort;
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
    $[51] = wave;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== showToggleableDropInput || $[54] !== wave) {
    t21 = showToggleableDropInput && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.2,
      ease: "easeInOut"
    }}><WaveDropCreate wave={wave} onCancel={() => {
        if (mountedRef.current) {
          setIsCreateDropOpen(false);
        }
      }} onSuccess={() => {
        if (mountedRef.current) {
          setIsCreateDropOpen(false);
        }
      }} /></motion.div>;
    $[53] = showToggleableDropInput;
    $[54] = wave;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== t21) {
    t22 = <AnimatePresence>{t21}</AnimatePresence>;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== showPersistentDropInput || $[59] !== wave) {
    t23 = showPersistentDropInput && <div className="tw-mt-2"><WaveDropCreate wave={wave} onSuccess={_temp3} isCurationLeaderboard={true} /></div>;
    $[58] = showPersistentDropInput;
    $[59] = wave;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== isMemesCreateOpen || $[62] !== isMemesWave || $[63] !== wave) {
    t24 = isMemesWave && isMemesCreateOpen && <MemesArtSubmissionModal isOpen={isMemesCreateOpen} wave={wave} onClose={() => setIsMemesCreateOpen(false)} />;
    $[61] = isMemesCreateOpen;
    $[62] = isMemesWave;
    $[63] = wave;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== leaderboardContent || $[66] !== t22 || $[67] !== t23 || $[68] !== t24) {
    t25 = <div>{t22}{t23}{t24}{leaderboardContent}</div>;
    $[65] = leaderboardContent;
    $[66] = t22;
    $[67] = t23;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== leaderboardViewStyle || $[71] !== t14 || $[72] !== t20 || $[73] !== t25) {
    t26 = <div className="tw-w-full tw-flex tw-flex-col tw-rounded-t-xl tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-overflow-x-hidden tw-flex-grow tw-px-2 sm:tw-px-4" style={leaderboardViewStyle}>{t14}{t20}{t25}</div>;
    $[70] = leaderboardViewStyle;
    $[71] = t14;
    $[72] = t20;
    $[73] = t25;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  return t26;
};
export default MyStreamWaveLeaderboard;
function _temp(value) {
  return value === WaveDropsLeaderboardSort.RANK || value === WaveDropsLeaderboardSort.RATING_PREDICTION || value === WaveDropsLeaderboardSort.TREND || value === WaveDropsLeaderboardSort.MY_REALTIME_VOTE || value === WaveDropsLeaderboardSort.CREATED_AT;
}
function _temp2(group) {
  return group.id;
}
function _temp3() {}